import { useQuery } from "@tanstack/react-query";
import { getMonth, startOfMonth, subMonths } from "date-fns";
import { motion } from "motion/react";
import type React from "react";
import { useMemo, useRef, useState } from "react";
import { twJoin } from "tailwind-merge";

import type {
  EngagedResidentsMonthlyPercentageDto,
  MonthlyReportDateRangeProjectEngagementPercentageItemDto,
} from "@/api/types";
import { Button } from "@/components/Button/Button";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useBool } from "@/hooks/useBool";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import { useSlug } from "@/hooks/useSlug";
import { ChartLegend } from "@/modules/analytics/components/utility/ChartLegend";
import { dataColors } from "@/modules/analytics/theme";
import { useAnalyticsQueries } from "@/queries/analytics";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

import { AnalyticsWidgetCard } from "../AnalyticsWidgetCard";
import { SocialContributionWidgetActionsModal } from "./SocialContributionWidgetActionsModal";

const dateNow = new Date();
const FALLBACK_PRIMARY_DATA: Pick<
  MonthlyReportDateRangeProjectEngagementPercentageItemDto,
  "engagedResidentPercentage" | "engagedResident" | "totalContributionCount" | "range"
> = {
  range: {
    from: startOfMonth(subMonths(dateNow, 1)).toISOString(),
    to: startOfMonth(dateNow).toISOString(),
    month: getMonth(subMonths(dateNow, 1)) + 1,
    year: dateNow.getFullYear(),
  },
  engagedResidentPercentage: 0,
  engagedResident: 0,
  totalContributionCount: 0,
};
const FALLBACK_BENCHMARK_PERCENTAGE = 0.0;
const FALLBACK_PERIOD: EngagedResidentsMonthlyPercentageDto["period"] = "zeroToOneMonth";

export function SocialContributionWidget(): React.ReactNode {
  const [isActionModalOpened, actionModalHandler] = useBool(false);
  const [chartWidth, setChartWidth] = useState(0);

  const refChartContainer = useRef<HTMLDivElement>(null);

  useResizeObserver(refChartContainer, (entries) => {
    for (const entry of entries) {
      if (entry.contentRect) {
        setChartWidth(entry.contentRect.width);
      }
    }
  });

  const slug = useSlug();
  const { t, i18n } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const {
    data: socialContributionStats,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    ...analyticsQueries.getSocialContributions("lastMonth"),
    select: commonAPIDataSelector,
  });

  const primaryData = socialContributionStats?.data[0] || FALLBACK_PRIMARY_DATA;
  const benchmarkPercentage =
    socialContributionStats?.benchmark[0].engagedResidentPercentage || FALLBACK_BENCHMARK_PERCENTAGE;
  const period = socialContributionStats?.period || FALLBACK_PERIOD;
  const isDataAvailable = !isPending && !isError && Boolean(socialContributionStats.data[0]);
  const isActionRequired =
    !isPending && !isError && isDataAvailable && primaryData.engagedResidentPercentage < benchmarkPercentage;

  const bars = useMemo(() => {
    // Position of the benchmark is always 70% of the chart width
    const posBenchmark = 0.7 * chartWidth;
    // Amount of the chart width that is on either side of the benchmark
    const leftWidth = posBenchmark;
    const rightWidth = chartWidth - leftWidth;
    // Width per percentage on either side side of the benchmark (e.g. 2px per percentage)
    // This differs depending on the exact benchmark percentage
    const stepLeft = leftWidth / (benchmarkPercentage || 0.001); // Avoid division by zero
    const stepRight = rightWidth / (100 - benchmarkPercentage);
    // Start positions of the bars
    const posStartRed = 0;
    const posStartOrange = 0.1 * benchmarkPercentage * stepLeft; // 10% benchmark
    const posStartYellow = 0.3 * benchmarkPercentage * stepLeft; // 30% benchmark
    const posStartGreen = 0.9 * benchmarkPercentage * stepLeft; // 90% benchmark
    // End positions of the bars
    const posEndRed = posStartOrange;
    const posEndOrange = posStartYellow;
    const posEndYellow = posStartGreen;
    const posEndGreen = chartWidth;

    // Position of the project analytics depending on which side of the benchmark it falls on
    const posPrimaryDataValue =
      primaryData.engagedResidentPercentage <= benchmarkPercentage
        ? primaryData.engagedResidentPercentage * stepLeft
        : (primaryData.engagedResidentPercentage - benchmarkPercentage) * stepRight + posBenchmark;

    return {
      posStartRed,
      posStartOrange,
      posStartYellow,
      posStartGreen,
      posEndRed,
      posEndOrange,
      posEndYellow,
      posEndGreen,
      posPrimaryDataValue,
    };
  }, [chartWidth, primaryData.engagedResidentPercentage, benchmarkPercentage]);

  return (
    <>
      <AnalyticsWidgetCard.Root
        title={t("component.analytics-widget.social-contribution.title")}
        href={isDataAvailable ? routes.analytics.contributionDetails({ slug }) : undefined}
        isError={isError}
        isLoading={isPending}
        isDataAvailable={isDataAvailable}
        error={error}
        onReload={refetch}
      >
        <AnalyticsWidgetCard.Body>
          <div className="my-2 flex h-40 w-full items-end overflow-x-hidden" aria-label="Social contribution chart">
            <div className="flex w-full flex-col gap-1">
              <div ref={refChartContainer} className="relative w-full ">
                {/* Bars */}
                <ul className="relative flex w-full [&>li]:absolute [&>li]:bottom-0 [&>li]:h-6">
                  <li
                    style={{
                      width: `${bars.posEndRed - bars.posStartRed}px`,
                      transform: `translateX(${bars.posStartRed}px)`,
                    }}
                    className="bg-red-500"
                  />
                  <li
                    style={{
                      width: `${bars.posEndOrange - bars.posStartOrange}px`,
                      transform: `translateX(${bars.posStartOrange}px)`,
                    }}
                    className="bg-orange"
                  />
                  <li
                    style={{
                      width: `${bars.posEndYellow - bars.posStartYellow}px`,
                      transform: `translateX(${bars.posStartYellow}px)`,
                    }}
                    className="bg-yellow-500"
                  />
                  <li
                    style={{
                      width: `${bars.posEndGreen - bars.posStartGreen}px`,
                      transform: `translateX(${bars.posStartGreen}px)`,
                    }}
                    className="bg-green-500"
                  />
                  {/* Mask to block the unfilled part of the chart */}
                  <motion.span
                    variants={{
                      initial: { x: 0 },
                      animate: { x: bars.posPrimaryDataValue },
                    }}
                    initial="initial"
                    animate={isDataAvailable ? "animate" : "initial"}
                    transition={{ duration: 0.8, ease: [0.4, 0, 0, 0.99], delay: 0.2 }}
                    style={{
                      width: chartWidth,
                    }}
                    className="absolute bottom-[2px] z-10 h-[calc(24px-2px)] bg-white"
                  >
                    {/* Project value indicator  */}
                    <div className="absolute -bottom-2px z-20 flex w-1 flex-col items-center gap-1">
                      <motion.h4
                        variants={{
                          initial: { opacity: 0 },
                          animate: { opacity: 1 },
                        }}
                        initial="initial"
                        animate={isDataAvailable ? "animate" : "initial"}
                        transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                        className={twJoin(
                          "text-headline1 text-aop-dark-blue-500",
                          primaryData.engagedResidentPercentage === 100 && "translate-x-[-42px]",
                          primaryData.engagedResidentPercentage > 95 &&
                            primaryData.engagedResidentPercentage < 100 &&
                            "translate-x-[-36px]",
                          primaryData.engagedResidentPercentage > 90 &&
                            primaryData.engagedResidentPercentage <= 95 &&
                            "translate-x-[-32px]",
                          primaryData.engagedResidentPercentage > 85 &&
                            primaryData.engagedResidentPercentage <= 90 &&
                            "translate-x-[-28px]",
                          primaryData.engagedResidentPercentage > 80 &&
                            primaryData.engagedResidentPercentage <= 85 &&
                            "translate-x-[-24px]",
                          primaryData.engagedResidentPercentage > 75 &&
                            primaryData.engagedResidentPercentage <= 80 &&
                            "translate-x-[-20px]",
                        )}
                      >
                        {primaryData.engagedResidentPercentage}%
                      </motion.h4>
                      <span className="h-8 w-1 bg-aop-dark-blue-500" />
                    </div>
                  </motion.span>
                </ul>
              </div>
              {/* Labels */}
              <div className="flex items-center justify-between">
                <span aria-label="0%" className="text-overline text-grey-700">
                  0
                </span>
                <span aria-label="100%" className="text-overline text-grey-700">
                  100
                </span>
              </div>
            </div>
          </div>

          <ChartLegend
            items={[
              {
                label: (
                  <p className="text-body">
                    <Trans
                      i18nKey="component.analytics-widget.social-contribution.chart.legend.primary"
                      components={{
                        bold: <span className="text-body-bold" />,
                      }}
                      values={{
                        date: formatDate(i18n, "monthYear", primaryData.range.from),
                      }}
                    />
                  </p>
                ),
                color: dataColors.primary,
              },
            ]}
          />
        </AnalyticsWidgetCard.Body>
        <AnalyticsWidgetCard.Footer>
          {isActionRequired && (
            <Button onClick={actionModalHandler.setTrue}>
              {t("component.analytics-widget.social-contribution.action-btn")}
            </Button>
          )}
          <AnalyticsWidgetCard.ReferenceLabel
            currValue={primaryData.engagedResidentPercentage || 0}
            referenceValue={benchmarkPercentage}
          />
          <AnalyticsWidgetCard.BenchmarkInfo benchmark={benchmarkPercentage} period={period} />
        </AnalyticsWidgetCard.Footer>
      </AnalyticsWidgetCard.Root>
      <SocialContributionWidgetActionsModal isOpened={isActionModalOpened} onOpenChange={actionModalHandler.set} />
    </>
  );
}
